/**
 * Elegir el cliente de una lista (SPEC-029 §5, P-2).
 *
 * Lorus escribió «Consumidor Final» de cinco formas —«CONSU», «CON», «cons»…—
 * y el 96 % de sus etiquetas quedaron sin documento. Cada grafía aprendía por
 * su cuenta. Esto busca entre los clientes que ya tienen documento, para que se
 * elijan en vez de teclearse.
 */

export interface Cliente {
  nombre: string;
  nit: string;
}

export interface ClienteElegido extends Cliente {
  /** Lo escribió la persona y no estaba en la lista (D-70). */
  nuevo: boolean;
}

export const CONSUMIDOR_FINAL: Cliente = { nombre: "Consumidor Final", nit: "222222222222" };

const limpio = (s: string) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const digitos = (s: string) => String(s ?? "").replace(/\D/g, "");

/**
 * Los clientes que casan con lo tecleado: por nombre —cada palabra tecleada es
 * el principio de alguna del nombre, sin tildes ni mayúsculas— o por documento.
 * Primero los que empiezan igual.
 */
export function buscarClientes(lista: Cliente[], q: string, max = 8): Cliente[] {
  const t = limpio(q);
  if (!t) return [];
  const palabras = t.split(" ");
  const d = digitos(q);
  const casa = (c: Cliente) => {
    const nombre = limpio(c.nombre).split(" ");
    const porNombre = palabras.every((p) => nombre.some((n) => n.startsWith(p)));
    const porDocumento = d.length >= 3 && digitos(c.nit).includes(d);
    return porNombre || porDocumento;
  };
  const empiezaIgual = (c: Cliente) => (limpio(c.nombre).startsWith(t) ? 0 : 1);
  return lista
    .filter(casa)
    .sort((a, b) => empiezaIgual(a) - empiezaIgual(b) || a.nombre.localeCompare(b.nombre, "es"))
    .slice(0, max);
}

/** ¿Lo tecleado es exactamente un cliente de la lista? Entonces no es uno nuevo. */
export function clienteExacto(lista: Cliente[], q: string): Cliente | null {
  const t = limpio(q);
  return lista.find((c) => limpio(c.nombre) === t) ?? null;
}
